import { Injectable } from '@angular/core';
import axios from 'axios';
import { serialize } from 'object-to-formdata';
import { AdminCaseTableItem } from 'src/app/components/dashboard/cases/cases.component';
import { CaseTableItem } from 'src/app/shared/payee-cases/types';
import { environment } from 'src/environments/environment';
import { LawFirmItem } from '../law-firm/law-firm.service';
import { Representative } from '../representatives/representatives.service';
import { CaseType } from 'src/app/helper/edit-case-dialog/edit-case-dialog.service';

type LegalRelationshipFile = {
  id: string;
  file_name: string;
  storage_url: string;
};

type CreateCaseParams = {
  client_id: string;
  jurisdiction: string;
  product_type: string;
}

export const INTEREST_TYPE = {
  STATUTORY: 'statutory',
  CONTRACTUAL: 'contractual',
} as const;
export type InterestType = typeof INTEREST_TYPE[keyof typeof INTEREST_TYPE];

export const externalIdTypes = [
  'ofp',
  'enforcement',
  'bailiff',
] as const;
export type ExternalIdType = typeof externalIdTypes[number];

export type Address = {
  country_id: number | null;
  postcode: string | null;
  settlement: string | null;
  street: string | null;
};

export type BankAccount = {
  account_holder_name: string | null;
  iban: string | null;
  account_number: string | null;
  bic: string | null;
  bank_name: string | null;
  lock_bank_account?: boolean;
};

export type GetEditCaseResponse = {
  payee_case: {
    payee_case_reference_id: string;
    product_type: 'notice'
    | 'demand_letter'
    | 'hard_1'
    | 'hard_2';
    legal_relationship_id: number;
    legal_relationship_files: LegalRelationshipFile[];
    legal_relationship_specification: string;
    legal_relationship_explanation: string;
    description: string;
    external_ids: {
      type: ExternalIdType;
      value: string;
    }[];
    is_szamlazzhu: boolean;
  };
  claims: {
    id: string;
    files: {
      id: string;
      file_name: string;
      storage_url: string;
      invoice_number: string | null;
      invoice_date: string | null;
      case_type: CaseType | null;
    }[];
    due_date_at: string;
    original_amount: number;
    remaining_amount: number;
    currency_iso: string;
    claim_type_id: number | null;
    claim_type_specification: string | null;
    interest_claim: {
      rate: string | null;
      remaining_amount: number | null;
      original_amount: number | null;
    } | null;
    ref_id: null | string;
  }[];
  interest_type: InterestType | null;
  client: {
    name: string;
    address: Address | null;
    tax_number: string | null;
    party_type_id: number | null;
    email: string | null;
    finance_email: string | null;
    phone: string | null;
    lock_representatives: boolean;
    lock_bank_account: boolean;
    company_updated_at: string | null;
    is_duplicated: boolean;
    representatives: Representative[];
    bank_account: BankAccount | null;
    client_data: {
      birth_date: string | null;
      birth_place: string | null;
      mother_name: string | null;
      registration_number: string | null;
    };
  };
  debtors: {
    id: string;
    name: string;
    address: Address | null;
    bank_account: BankAccount | null;
    tax_number: string | null;
    registration_number: string | null;
    party_type_id: number | null;
    email: string | null;
    phone: string | null;
    mother_name: string | null;
    birth_name: string | null;
    birth_place: string | null;
    birth_date: string | null;
    personal_id: string | null;
    employer_name: string | null;
    company_updated_at: string | null;
    lock_representatives: boolean;
    lock_bank_account: boolean;
    representatives: Representative[];
  }[];
  law_firms: LawFirmItem[];
};

type UpdateAddress = {
  country_id: number | null;
  postcode: string | null;
  settlement: string | null;
  street: string | null;
};

export type UpdateRepresentative = {
  id: null | number;
  name: null | string;
  tax_number: null | string;
  party_type_id: null | number;
  registration_number: null | string;
  registration_office_id: null | number;
  type_id: null | number;
  address: null | UpdateAddress;
  representatives: UpdateRepresentative[];
};

type UpdateBankAccount = {
  account_number: null | string;
  iban: null | string;
  bic: null | string;
  bank_name: null | string;
};

export type UpdateCaseParams = {
  legal_relationship: {
    files: {
      id: null | string;
      file: null | File;
    }[];
    specification: string | null;
    description: string | null;
    type_id: number | null;
    law_firms: number[];
    external_ids: {
      type: string;
      value: string;
    }[];
  };
  client: {
    name: string;
    tax_number: string | null;
    party_type_id: number | null;
    bank_account: null | UpdateBankAccount;
    address: null | UpdateAddress;
    client_data: {
      birth_date: string | null;
      birth_place: string | null;
      mother_name: string | null;
      registration_number: string | null;
    };
    email: string | null;
    finance_email: string | null;
    phone: string | null;
    lock_representatives: boolean;
    lock_bank_account: boolean;
    representatives?: UpdateRepresentative[];
  };
  claims: {
    id: null | string;
    ref_id?: string;
    files: {
      id: null | string;
      file: null | File;
    }[];
    due_date_at: null | string;
    remaining_amount: number;
    original_amount: number;
    currency_iso: string;
    type_id: null | number;
    specification: null | string;
    interest_type: null | InterestType;
    interest_claim?: {
      interest_rate: null | string;
      remaining_amount: number;
      original_amount: number;
    };
  }[];
  debtors: {
    id: string | null;
    name: string | null;
    tax_number: string | null;
    address: null | UpdateAddress;
    bank_account: null | UpdateBankAccount;
    debtor_data: {
      birth_date: null | string;
      birth_place: null | string;
      mother_name: null | string;
      birth_name: null | string;
      personal_id: null | string;
      registration_number: null | string;
      employer_name: null | string;
    };
    party_type_id: number | null;
    email: string | null;
    phone: string | null;
    lock_representatives: boolean;
    lock_bank_account: boolean;
    representatives?: UpdateRepresentative[];
  }[];
  interest_type: null | InterestType;
};

@Injectable({
  providedIn: 'root'
})
export class CasesService {
  async getCase(caseId: string): Promise<AdminCaseTableItem> {
    const url = environment.baseUrl + `/api/case/${caseId}`;
    const result = await axios.get<AdminCaseTableItem>(url);

    return result.data;
  }

  async getEditCaseData(caseId: string): Promise<GetEditCaseResponse> {
    const url = environment.baseUrl + `/api/case/${caseId}`;
    const result = await axios.get<GetEditCaseResponse>(url);

    return result.data;
  }

  async createCase(params: CreateCaseParams): Promise<CaseTableItem> {
    const url = environment.baseUrl + '/api/case';
    const result = await axios.post<CaseTableItem>(url, params);

    return result.data;
  }

  async updateCase(caseId: string, params: UpdateCaseParams): Promise<GetEditCaseResponse> {
    const url = environment.baseUrl + `/api/case/${caseId}`;
    const formData = serialize(params, {
      indices: true,
      booleansAsIntegers: true,
    });
    formData.append('_method', 'PUT');
    const result = await axios.post<GetEditCaseResponse>(url, formData);

    return result.data;
  }

  async copyCaseWithProduct(caseId: string, productId: number): Promise<CaseTableItem> {
    const url = environment.baseUrl + `/api/copy-case-with-product/${caseId}/${productId}`;
    const result = await axios.post<CaseTableItem>(url);

    return result.data;
  }

  async getAllowedLegalRelationships(claim_type_ids: number[]): Promise<number[]> {
    const queryParms = claim_type_ids.map(id => `claim_type_ids[]=${id}`).join('&');
    const url = environment.baseUrl + `/api/allowed-legal-relationship?${queryParms}`;
    const result = await axios.get<number[]>(url);

    return result.data;
  }

  async restoreCase(caseId: string): Promise<void> {
    const url = environment.baseUrl + `/api/case/${caseId}/restore`;
    await axios.post(url);
  }

  async resendProForma(caseId: string): Promise<void> {
    const url = environment.baseUrl + `/api/payee-case/${caseId}/resend-pro-forma`;
    await axios.post(url);
  }

}
